import React, { useState } from "react";
import type { ReactNode } from "react";
import { useAuth0 } from "utils/react-auth0-wrapper";
import AccessTokenStorage from "utils/AccessTokenStorage";
import AuthApolloInitializer from "utils/AuthApolloInitializer";

const AuthApolloProviderComponent = ({
  children,
}: {
  children?: ReactNode;
}) => {
  const { loading, getTokenSilently, loginWithRedirect, logout } = useAuth0();
  const [tokenLoading, setTokenLoading] = useState(true);
  const [timeoutId, setTimeoutId] = useState<ReturnType<typeof setTimeout>>();
  const initializerProps = {
    tokenLoading,
    loading,
    getTokenSilently,
    loginWithRedirect,
    logout,
  };

  const getTokenExpiration = (token: string) => {
    const decodedToken = JSON.parse(atob(token.split(".")[1])) as {
      exp: number;
    };
    return new Date(decodedToken.exp * 1000);
  };

  // If Auth0 is done initializing, the begin out provider init, otherwise just provide tokenLoading state
  if (loading) {
    return (
      <AuthApolloInitializer {...initializerProps}>
        {children}
      </AuthApolloInitializer>
    );
  }

  const getAccessToken = async () => {
    try {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      // Since AUTH_TOKEN relies on a 3rd party service, it might be slow to show up
      // getTokenSilently resolves undefined on the logged-out path; the original
      // passes the result straight through and relies on this try/catch.
      const tokenResult = (await getTokenSilently()) as string;
      AccessTokenStorage.setToken(tokenResult);
      const now = new Date();
      const tokenExpiration = getTokenExpiration(tokenResult);
      const tenSeconds = 10 * 1000;
      const timeUntilRefresh =
        tokenExpiration.getTime() - now.getTime() - tenSeconds;
      const currentTimeoutId = setTimeout(() => {
        // refreshes the token after `timeUntilRefresh` has elapsed
        void getAccessToken();
      }, timeUntilRefresh);
      setTimeoutId(currentTimeoutId);
    } catch (e) {
      console.log(e);
    } finally {
      // We have an Auth Token, can continue loading the app
      setTokenLoading(false);
    }
  };

  // Only request the Auth Token if we don't have it yet
  if (tokenLoading && !AccessTokenStorage.getToken()) {
    void getAccessToken();
  }

  if (tokenLoading) {
    return (
      <AuthApolloInitializer {...initializerProps}>
        {children}
      </AuthApolloInitializer>
    );
  }

  return (
    <AuthApolloInitializer {...initializerProps}>
      {children}
    </AuthApolloInitializer>
  );
};

export const AuthApolloProvider = AuthApolloProviderComponent;
